
import React, { useEffect, type JSX } from "react";
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { Checkbox, DialogContentText, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import { IEntityValueTagModel } from "../../common/models/IEntityValueModels";
import _ from "lodash";
import {isMobile, isTablet} from 'react-device-detect';

export type ConfirmWriteDownDialogValues = IEntityValueTagModel[];

export type ConfirmWriteDownDialogResult = DialogResult<ConfirmWriteDownDialogValues>;

export type ConfirmWriteDownDialogProps = DialogProps<ConfirmWriteDownDialogValues, ConfirmWriteDownDialogResult>;

export function ConfirmWriteDownDialog({
    open,
    onClose,
    values
}: ConfirmWriteDownDialogProps): JSX.Element {

    const { t } = useTranslation();
    const theme = useTheme();

    //const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const isPhone = isMobile && !isTablet;
    const [hasChanges, setHasChanges] = useState(true);
    const [tags, setTags] = useState<IEntityValueTagModel[]>([]);

    const onToggleItem = (item: IEntityValueTagModel) => {

        const items = _.cloneDeep(tags);
        const tag = items.find(o => o.id === item.id);
        if (tag) {
            tag.selected = !item.selected;
        }

        setTags(items);

        setHasChanges(items.find(o => o.selected) !== undefined);
    }

    const onOk = () => {
        onClose && onClose({
            reason: "ok",
            values: tags.filter(o => o.selected)
        });
    };

    const onCancel = () => {

        onClose && onClose({ 
            reason: "cancel", 
            values: undefined 
        });
    };

    useEffect(() => {
        if (values) {
            setTags(values);
        }
    }, [values,
        setTags]);

    return (
        <ResponsiveDialog
            title={t('livedata.reviewChanges')}
            enableOkay={hasChanges}
            okayLabel={t("buttons.submit")}
            open={open}
            onClose={
                (reason: string) => {
                    reason === "ok"
                        ? onOk()
                        : onCancel()
                }
            }>
            <DialogContentText>
                {t('livedata.reviewChangesDesc')}
            </DialogContentText>
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>


            {tags.map((item, index) => {
                const labelId = `checkbox-list-label-${index}`;
            
                return (
                    <React.Fragment key={item.id}>
                        <ListItem 
                            alignItems="flex-start" 
                            onClick={() => onToggleItem(item)} 
                            dense={true}
                            sx={{
                                padding: isPhone ? 0 : undefined
                            }}>

                        <ListItemButton role={undefined} 
                            sx={{
                                padding: isPhone ? 0 : undefined
                            }} >
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={item.selected}
                                    tabIndex={-1}
                                    disableRipple
                                    slotProps={{
                                        input: { 'aria-labelledby': labelId }
                                    }}
                                />
                            </ListItemIcon>

                            <ListItemText
                                id={labelId}
                                primary={item.displayText}
                                sx={{fontWeight: 600}}
                                disableTypography={true}
                                secondary={
                                    
                                !isPhone ? 
                                <Stack direction='row' spacing={3}>
                                     <Typography
                                         component="span"
                                         sx={{
                                             color: "text.primary",
                                             marginRight: theme.spacing(4)
                                         }}>
                                             {t('livedata.currentvalue')}: {item.originalTagValue && item.originalTagValue?.displayText ? `${item.originalTagValue?.displayText} ${item.tagValue?.unitDisplayName}` : '...'}
                                     </Typography>
                                     <Typography
                                         component="span"
                                         sx={{
                                             color: "secondary.main"
                                         }}>
                                             {t('livedata.newvalue')}: {`${item.tagValue?.displayText}  ${item.tagValue?.unitDisplayName ?? ''}`}
                                     </Typography>

                                    
                                </Stack> :
                                <Stack direction='column'>
                                    <Stack direction='row'>
                                        <Typography
                                            component="span"
                                            sx={{
                                                flexBasis: '50%',
                                                color: "text.primary"
                                            }}>
                                            {t('livedata.currentvalue')}:
                                     </Typography>
                                     <Typography
                                            component="span"
                                            sx={{
                                                color: "text.primary"
                                            }}>
                                            {item.originalTagValue ?  `${item.originalTagValue?.displayText} ${item.tagValue?.unitDisplayName}` : '...'}
                                     </Typography>
                                    </Stack>
                                    <Stack direction='row'>
                                        <Typography
                                            component="span"
                                            sx={{
                                                flexBasis: '50%',
                                                color: "secondary.main"
                                            }}>
                                            {t('livedata.newvalue')}:
                                     </Typography>
                                     <Typography
                                            component="span"
                                            sx={{
                                                color: "secondary.main"
                                            }}>
                                            {`${item.tagValue?.displayText}  ${item.tagValue?.unitDisplayName}`}
                                     </Typography>

                                    </Stack>

                                </Stack>
                            }/>
            
                        </ListItemButton>                            

                        </ListItem>
                        <Divider variant="fullWidth" component="li" />
                    </React.Fragment>
                );
                })}                    
            </List>
        </ResponsiveDialog>
    );
}

export type ConfirmWriteDownDialogState = DialogState<ConfirmWriteDownDialogValues, ConfirmWriteDownDialogResult>;
export type BindConfirmWriteDownDialogReturn = BindDialogReturn<ConfirmWriteDownDialogValues, ConfirmWriteDownDialogResult>;

export function useConfirmWriteDownDialogState(): ConfirmWriteDownDialogState {
    return useDialogState();
}

export function bindConfirmWriteDownDialog(props: ConfirmWriteDownDialogState): BindConfirmWriteDownDialogReturn {
    return bindDialog(props);
}
